import {Component, OnInit} from '@angular/core';
import {Tenant} from '../../contracts/tenant.interface';
import {TenantService} from '../../services/tenant.service';

// UI Modules
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TableModule} from 'primeng/table';
import {ToastModule} from 'primeng/toast';
import {ToolbarModule} from 'primeng/toolbar';
import {InputTextModule} from 'primeng/inputtext';
import {TextareaModule} from 'primeng/textarea';
import {SelectModule} from 'primeng/select';
import {IconFieldModule} from 'primeng/iconfield';
import {InputIconModule} from 'primeng/inputicon';
import {DropdownModule} from 'primeng/dropdown';
import {filter, finalize, switchMap, take} from 'rxjs';
import {DataTableColumnButton, TableColumn} from '../../../shared/contracts/table-column.interface';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {ConfirmDialogComponent} from '../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import {DialogAction} from '../../../shared/contracts/dialog.interface';
import {NotificationService} from '../../../shared/services/notification.service';
import {TenantDialogComponent} from '../dialogs/tenant-dialog/tenant-dialog.component';
import {
  EntityCrudPageTemplateComponent
} from '../../../layout/entity-crud-page-template/entity-crud-page-template.component';
import {SettingsDialogComponent} from '../dialogs/settings-dialog/settings-dialog.component';
import {TenantSettingsService} from '../../services/tenant_settings.service';

@Component({
  selector: 'kc-tenants-list',
  templateUrl: 'tenants-list.component.html',
  styleUrls: ['tenants-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ToastModule,
    ToolbarModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    DropdownModule,
    IconFieldModule,
    InputIconModule,
    EntityCrudPageTemplateComponent,
  ],
  providers: [TenantService, DialogService, NotificationService]
})
export class TenantsListComponent implements OnInit {
  columns: TableColumn[] = [
    { field: 'logoMediaUrl', header: 'Logo', type: 'avatar'},
    { field: 'companyName', header: 'Azienda', sortable: false, filterable: true  },
    { field: 'vatNumber', header: 'P.Iva', sortable: false, filterable: true },
    { field: 'email', header: 'Email', sortable: false, filterable: true },
    { header: 'Azioni', buttons: <DataTableColumnButton[]>[
        {icon: 'pi-pencil', click: this.editTenant.bind(this), tooltip: 'Modifica', color: 'primary' },
        {icon: 'pi-cog', click: this.openSettings.bind(this), tooltip: 'Settings', color: 'primary' },
        {icon: 'pi-trash', click: this.deleteTenant.bind(this), tooltip: 'Elimina', color: 'danger' },
      ]}
  ];

  tenants: Tenant[] = [];
  loading = false;

  _refDeleteTenantsDialog: DynamicDialogRef | undefined;
  _refCreateUpdateTenantsDialog: DynamicDialogRef | undefined;
  _refSettingsTenantsDialog: DynamicDialogRef | undefined;

  constructor(
    private tenantService: TenantService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private tenantSettingsService: TenantSettingsService,
  ) {}

  ngOnInit(): void {
    this.loadTenants()
  }

  saveTenant(): void {
    this._refCreateUpdateTenantsDialog = this.dialogService.open(TenantDialogComponent, {
      ...TenantDialogComponent.DIALOG_CONFIG,
    });

    this._refCreateUpdateTenantsDialog.onClose
      .pipe(
        filter((result) => result?.action === DialogAction.OK),
        switchMap(result =>  this.tenantService.create(result.data))
      ).subscribe((result) => {
        this.loadTenants();
        this.notificationService.success("Azienda creata con successo.");
    });
  }

  editTenant(tenant: Tenant): void {
    this._refCreateUpdateTenantsDialog = this.dialogService.open(TenantDialogComponent, {
      ...TenantDialogComponent.DIALOG_CONFIG,
      data: tenant,
    });

    this._refCreateUpdateTenantsDialog.onClose
      .pipe(
        filter((result) => result?.action === DialogAction.OK),
        switchMap(result => this.tenantService.update(tenant.id!, result.data))
      ).subscribe(_ => {
        this.loadTenants();
      this.notificationService.success("Azienda modificata con successo.");
    });
  }

  deleteTenant(tenant: Tenant): void {
    this._refDeleteTenantsDialog = this.dialogService.open(ConfirmDialogComponent, {
      ...ConfirmDialogComponent.DIALOG_CONFIG,
      data: {message: `Sei sicuro di voler eliminare l'azienda ${tenant.companyName}`},
    });

    this._refDeleteTenantsDialog.onClose
      .pipe(
        filter((result) => result?.action === DialogAction.OK),
        switchMap(_ => this.tenantService.delete(tenant.id!)),
      ).subscribe((_) => {
      this.loadTenants();
      this.notificationService.success("Azienda eliminata con successo.");
    });
  }

  openSettings(tenant: Tenant): void {
    this._refSettingsTenantsDialog = this.dialogService.open(SettingsDialogComponent, {
      ...SettingsDialogComponent.DIALOG_CONFIG,
      data: {tenant: tenant},
    });

    this._refSettingsTenantsDialog.onClose
      .pipe(
        filter((result) => result?.action === DialogAction.OK),
        switchMap(result => this.tenantSettingsService.update(tenant.id!, result.data))
      ).subscribe((_) => {
      this.notificationService.success("Settings aggiornati con successo.");
    });
  }

  deleteSelectedTenants(tenants: Tenant[]): void {
    this.openDeleteTenantsDialog(tenants)
  }

  private openDeleteTenantsDialog(tenants: Tenant[]): void {
    this._refDeleteTenantsDialog = this.dialogService.open(ConfirmDialogComponent, {
      ...ConfirmDialogComponent.DIALOG_CONFIG,
      data: {message: `Sei sicuro di voler eliminare ${tenants.length} aziende?`,}
    });

    this._refDeleteTenantsDialog.onClose
      .pipe(
        filter((result) => result?.action === DialogAction.OK),
      ).subscribe((_) => {});
  }

  private loadTenants(): void {
    this.loading = true;
    this.tenantService.getAll().pipe(
      take(1),
      finalize(() => this.loading = false)
    ).subscribe(data => {
      this.tenants = data;
    });
  }
}
